import { FC, useEffect } from 'react';
import _ from 'lodash';
import { useQuery } from '@apollo/client';
import gql from 'graphql-tag';

import { useSelector, useDispatch, useI18n, useLicenses } from '~Hooks';

import { GetHotelIntegration, GetPaymentMethods, GetPaymentMethodsVariables } from './__queries__';
import { useGiftCardsContext } from '~Scenes/GiftCards/GiftCardsProvider';

const PAYMENT_METHODS_QUERY = gql`
  query GetPaymentMethods($companyId: ID!) {
    allPaymentMethods(filter: { company: { id: $companyId }, deleted: false }) {
      id
      internalId
      name
      accessLevels {
        id
      }
      venues {
        id
      }
      isPrimary
      maxAmount
    }
  }
`;

const HOTEL_INTEGRATION_QUERY = gql`
  query GetHotelIntegration {
    operativeViewer {
      id
      hasHotelIntegration
    }
  }
`;

const PaymentMethods: FC = () => {
  const venueId = useSelector((state) => state.venueId);
  const companyId = useSelector((state) => state.company);
  const paymentMethods = useSelector((state) => state.POSPaymentMethods);
  const dispatch = useDispatch();
  const { i18n } = useI18n();
  const { item } = useGiftCardsContext();
  const licenses = useLicenses();

  const { data } = useQuery<GetPaymentMethods, GetPaymentMethodsVariables>(PAYMENT_METHODS_QUERY, {
    pollInterval: 5 * 60000,
    fetchPolicy: 'cache-and-network',
    skip: !companyId,
    variables: {
      companyId,
    },
  });

  const hotelQuery = useQuery<GetHotelIntegration>(HOTEL_INTEGRATION_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !companyId,
  });
  const hasHotelIntegration = hotelQuery.data?.operativeViewer?.hasHotelIntegration ?? false;

  useEffect(() => {
    if (licenses.loading || !data) {
      return;
    }

    // Check if the company has the giftcard license and the venue has the giftcard item in menu
    const hasGiftcardLicense = licenses.hasGiftcard;
    const METHOD_ID = 'BAEMINGO_GIFTCARD';
    const isAlreadyActive = paymentMethods.some((m) => m.id === METHOD_ID);
    const hasRetain24 = data.allPaymentMethods.some((method) => method.internalId === 'RETAIN24');

    if (hasGiftcardLicense && !isAlreadyActive && !hasRetain24) {
      dispatch({
        type: 'ADD_PAYMENT_METHOD',
        method: {
          id: 'BAEMINGO_GIFTCARD',
          title: i18n('GiftCard.Payment'),
          active: true,
          accessLevels: [],
          venues: [],
        },
      });
    }
  }, [licenses.loading, item?.id, data]);

  /** Appends HOTEL_ROOM (charge the bill to a hotel room) once the venue is connected to a hotel. */
  useEffect(() => {
    const METHOD_ID = 'HOTEL_ROOM';
    if (!hasHotelIntegration || paymentMethods.some((m) => m.id === METHOD_ID)) {
      return;
    }

    dispatch({
      type: 'ADD_PAYMENT_METHOD',
      method: {
        id: METHOD_ID,
        title: i18n('HotelRoom.Title'),
        active: true,
        accessLevels: [],
        venues: [],
      },
    });
  }, [paymentMethods, hasHotelIntegration]);

  useEffect(() => {
    if (!data?.allPaymentMethods) {
      return;
    }

    const methods = data.allPaymentMethods
      // Prevent adding the same method twice
      ?.filter((method) => !paymentMethods.some((m) => m.id === method.internalId));

    methods.forEach(
      (method) => {
        dispatch({
          type: 'ADD_PAYMENT_METHOD',
          method: {
            id: method.internalId,
            title: method.name,
            active: true,
            accessLevels: method.accessLevels.map((a) => a.id),
            venues: method.venues.map((v) => v.id),
            isPrimary: method.isPrimary || false,
            maxAmount: method.maxAmount ?? null,
          },
        });
      },
      [data?.allPaymentMethods],
    );
  }, [data?.allPaymentMethods, venueId, paymentMethods]);
  return null;
};

export default PaymentMethods;
